import { RadioGroup as BaseRadioGroup } from "@headlessui/react"
import cc from "classcat"
import { useController } from "react-hook-form"
import type { Control } from "react-hook-form"
import { Label } from "components/atoms/form"

interface RadioOption {
  id: string
  label: string
}

interface RadioGroupProps {
  label: string
  name: string
  options: RadioOption[]
  control: Control
  required?: boolean
}

export const RadioGroup = ({
  label,
  name,
  options,
  control,
  required,
}: RadioGroupProps): JSX.Element => {
  const {
    field: { value, onChange, ref },
    fieldState: { error },
  } = useController({
    name,
    control,
    defaultValue: "",
    rules: required ? { required: true } : undefined,
  })

  return (
    <div className="block w-full sm:inline-flex">
      <div className="w-full">
        <BaseRadioGroup value={value} onChange={onChange} ref={ref}>
          <Label label={label} required={required} error={error} />
          <div
            className={cc([
              "divide-y divide-zinc-200 overflow-hidden rounded-lg border",
              error && !value ? "border-red-300" : "border-zinc-300",
            ])}
          >
            {options.map((option) => (
              <BaseRadioGroup.Option key={option.id} value={option.id}>
                {({ checked }) => (
                  <div
                    className={cc([
                      "flex cursor-pointer items-start space-x-3 px-4 py-3 transition-colors",
                      checked ? "bg-kkm-green/10" : "bg-white hover:bg-zinc-50",
                    ])}
                  >
                    <span
                      className={cc([
                        "mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full border-2 transition-colors",
                        checked
                          ? "border-kkm-green bg-kkm-green"
                          : "border-zinc-300 bg-white",
                      ])}
                    >
                      {checked && (
                        <span className="h-2 w-2 rounded-full bg-white" />
                      )}
                    </span>
                    <BaseRadioGroup.Label
                      as="span"
                      className="cursor-pointer text-sm font-medium text-zinc-800"
                    >
                      {option.label}
                    </BaseRadioGroup.Label>
                  </div>
                )}
              </BaseRadioGroup.Option>
            ))}
          </div>
        </BaseRadioGroup>
      </div>
    </div>
  )
}
